import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import { post } from "../../../api/util";
import * as Yup from "yup";
import { AutoCompleteItem } from "../../autocomplete/interface";
import { format, isBefore } from "date-fns";
import {
  cprMatchesBirthdate,
  requiredIfPredicate,
} from "../../../util/validation";
import { AdresseDTO } from "../../../interfaces/adresse";
import { validationMessages } from "../../../constants/validation";
import {
  IAdresseGeneric,
  PersonKontaktAdresseDTO,
} from "../../../interfaces/formDto";
import { YupAdresseGeneric } from "../../../util/yup";
import { stripTime } from "../../../util/date";

export function useOpretPerson(
  setIsKoenManuallyChanged: (boolean: boolean) => void,
) {
  const [step, setStep] = useState(1);
  const step1 = useFormik<OpretPersonStep1Formik>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      createPerson: CreatePersonKodeDict.ovrig,
      fornavne: "",
      efternavn: "",
      foedselsDato: undefined,
      cprNr: "",
      tlfMobilNr: "",
      koen: "",
    },
    validationSchema: Yup.object().shape({
      createPerson: Yup.object().required("Personart skal være sat"),
      fornavne: Yup.string().required("Fornavne skal være sat"),
      efternavn: Yup.string().required("Efternavn skal være sat"),
      foedselsDato: Yup.date()
        .nullable()
        .when("createPerson", {
          is: (createPerson: any) =>
            createPerson.kode !== CreatePersonKodeDict.ovrig.kode,
          then: (schema) => schema.required("Fødselsdato skal være sat"),
        })
        .test("foedselsDato", "Årstal skal være mindst 1900", (item) => {
          if (!item) return true;
          return item.getFullYear() > 1899;
        })
        .test("foedselsDato", "Må ikke være i fremtiden", (item) => {
          if (!item) return true;
          const date = new Date(item);
          return isBefore(date, new Date());
        }),
      cprNr: Yup.string()
        .matches(/^[0-9]+$/, "CPR-nummer skal kun indeholde tal")
        .min(4, "CPR-nummer skal være 4 cifre")
        .max(4, "CPR-nummer skal være 4 cifre")
        .test("cpr", "CPR matcher ikke fødselsdato", (item) =>
          cprMatchesBirthdate(item, step1.values.foedselsDato),
        ),
      tlfMobilNr: Yup.string(),
    }),
    onSubmit: () => {
      setStep(2);
    },
  });
  const step2 = useFormik<OpretPersonStep2Formik>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      harFirmaRelation: true,
      firma: undefined,
      emailArbejde: "",
      emailPrivat: "",
      harDanskAdresse: true,
      danskAdresse: undefined,
      andenAdresse: {
        adresselinie1: "",
        bynavn: "",
        landekode: "",
        postnummer: "",
      },
      startDato: undefined,
      firmaStartDato: undefined,
      firmaRelation: FirmaRelationKodeDict.ansat,
      principal: undefined,
      straksDeponering: false,
      udenlandskAdvokatSamfund: undefined,
      attention: "",
      bemaerkning: "",
      coNavn: "",
      periodeFraDato: undefined,
      periodeTilDatoInklusiv: undefined,
    },
    validationSchema: Yup.object().shape({
      straksDeponering: Yup.boolean(),
      firma: Yup.object().when("straksDeponering", {
        is: false,
        then: (schema) =>
          schema.test("firma", "Firma skal være sat", function (item) {
            return requiredIfPredicate(this.parent.harFirmaRelation, item);
          }),
      }),
      principal: Yup.object().test(
        "principal",
        "Principal skal være sat",
        function (item) {
          const { createPerson } = step1.values;
          if (
            createPerson.kode === CreatePersonKodeDict.advokatfuldmaegtig.kode
          ) {
            return item !== undefined;
          }
          return true;
        },
      ),
      danskAdresse: Yup.object().test(
        "danskAdresse",
        "Dansk adresse skal være sat",
        function () {
          const { harFirmaRelation, harDanskAdresse, danskAdresse } =
            this.parent;
          return requiredIfPredicate(
            step1.values.createPerson.kode ===
              CreatePersonKodeDict.ovrig.kode &&
              !harFirmaRelation &&
              harDanskAdresse,
            danskAdresse,
          );
        },
      ),
      andenAdresse: Yup.object().when("harDanskAdresse", {
        is: false,
        then: YupAdresseGeneric,
      }),
      startDato: Yup.date().test(
        "startDatoOvrig",
        validationMessages.required,
        function (item) {
          return requiredIfPredicate(
            step1.values.createPerson.kode !== "Oevrig",
            item,
          );
        },
      ),
      firmaStartDato: Yup.date()
        .nullable()
        .test(
          "firmaStartDatoAdvokatfuldFremtid",
          "Må ikke være efter startdato",
          function (value) {
            const { startDato } = this.parent;
            const createPersonKode = (
              step1.values.createPerson as CreatePersonKode
            ).kode;

            if (
              createPersonKode === "Advokatfuldmaegtig" ||
              createPersonKode === "Advokat" ||
              createPersonKode === "EuAdvokat"
            ) {
              if (!this.parent.straksDeponering && (!startDato || !value)) {
                return this.createError({
                  message: "Alle datofelter skal være udfyldt",
                });
              }
              if (!startDato || !value) return true;
              const valueDate = stripTime(new Date(value));
              const startDate = stripTime(new Date(startDato));
              return valueDate.valueOf() <= startDate.valueOf();
            }
            return true;
          },
        )
        .test("firmaStart", validationMessages.required, function (item) {
          if (
            step1.values.createPerson.kode === "Oevrig" &&
            !step2.values.harFirmaRelation
          ) {
            return true;
          }
          return requiredIfPredicate(!this.parent.straksDeponering, item);
        }),
      emailArbejde: Yup.string().email("E-mail har forkert format"),
      udenlandskAdvokatSamfund: Yup.object().test(
        "udenlandskAdvokatSamfund",
        "Udenlandsk advokatsamfund skal være sat",
        (item) =>
          requiredIfPredicate(
            step1.values.createPerson.kode ===
              CreatePersonKodeDict.euadvokat.kode,
            item,
          ),
      ),
      periodeFraDato: Yup.date()
        .nullable()
        .nullable()
        .test("periodeFraDato", "Skal være sat", function (periodeFraDato) {
          if (!this.parent.harFirmaRelation && !periodeFraDato) return false;
          return true;
        }),
      bemaerkning: Yup.string().nullable(),
      emailPrivat: Yup.string().email("E-mail har forkert format"),
    }),
    onSubmit: () => {
      opretPersonQuery.mutate({ step1: step1.values, step2: step2.values });
      setIsKoenManuallyChanged(false);
      setStep(3);
    },
  });

  const opretPersonQuery = useMutation({
    mutationFn: (form: {
      step1: OpretPersonStep1Formik;
      step2: OpretPersonStep2Formik;
    }) => {
      switch (form.step1.createPerson.kode) {
        case CreatePersonKodeDict.ovrig.kode: {
          const body = getOpretOevrigPersonDTO(form);
          return post<{ id: string; fornavne: string; efternavn: string }>({
            query: "person",
            body,
          });
        }
        case CreatePersonKodeDict.advokatfuldmaegtig.kode: {
          const body = getAdvokatFuldmaegtigPersonDTO(form);
          return post<{ id: string; fornavne: string; efternavn: string }>({
            query: "person",
            body,
          });
        }
        case CreatePersonKodeDict.advokat.kode: {
          const body = getAdvokatPersonDTO(form);
          return post<{ id: string; fornavne: string; efternavn: string }>({
            query: "person",
            body,
          });
        }
        case CreatePersonKodeDict.euadvokat.kode: {
          const body = getEUAdvokatPersonDTO(form);
          return post<{ id: string; fornavne: string; efternavn: string }>({
            query: "person",
            body,
          });
        }
        default:
          throw new Error(
            "Unknown CreatePersonKode " + form.step1.createPerson.kode,
          );
      }
    },
  });

  return { step, setStep, opretPersonQuery, step1, step2 };
}

const getOpretOevrigPersonDTO = (form: {
  step1: OpretPersonStep1Formik;
  step2: OpretPersonStep2Formik;
}): OpretPersonDTO => {
  const { step1, step2 } = form;
  const { cprNr, efternavn, foedselsDato, fornavne, tlfMobilNr, koen } = step1;
  const {
    harDanskAdresse,
    firma,
    firmaRelation,
    emailArbejde,
    danskAdresse,
    andenAdresse,
    bemaerkning,
    coNavn,
    periodeFraDato,
    periodeTilDatoInklusiv,
    firmaStartDato,
    startDato,
    harFirmaRelation,
    emailPrivat,
  } = step2;

  return {
    efternavn,
    fornavne,
    tlfMobilNr,
    emailPrivat: emailPrivat ?? null,
    createPerson: "Oevrig",
    foedselsDato: foedselsDato ? format(foedselsDato, "yyyy-MM-dd") : null,
    startDato: startDato ? format(startDato, "yyyy-MM-dd") : undefined,
    personFirma:
      harDanskAdresse && firma
        ? {
            adresseStandard: true,
            firmaId: firma?.id,
            periodeFraDato: firmaStartDato
              ? format(firmaStartDato, "yyyy-MM-dd")
              : "",
            personfirmatypeNr: firmaRelation.kode,
            emailArbejde: emailArbejde ? emailArbejde : undefined,
          }
        : undefined,
    cprnummer:
      cprNr && foedselsDato
        ? `${format(foedselsDato, "ddMMyy")}-${cprNr}`
        : null,
    principalId: null,
    udenlandskAdvokatsamfundId: null,
    personKontaktadresse: !harFirmaRelation
      ? {
          adresse:
            harDanskAdresse && danskAdresse
              ? { ...danskAdresse, coNavn }
              : (andenAdresse ?? null),
          bemaerkning: bemaerkning?.length ? bemaerkning : null,
          periodeFraDato: periodeFraDato
            ? format(periodeFraDato, "yyyy-MM-dd")
            : null,
          periodeTilDatoInklusiv: periodeTilDatoInklusiv
            ? format(periodeTilDatoInklusiv, "yyyy-MM-dd")
            : null,
        }
      : null,
    koen: koen !== "" ? koen : null,
  };
};
const getAdvokatFuldmaegtigPersonDTO = (form: {
  step1: OpretPersonStep1Formik;
  step2: OpretPersonStep2Formik;
}): OpretPersonDTO => {
  const { step1, step2 } = form;
  const { cprNr, efternavn, foedselsDato, fornavne, tlfMobilNr, koen } = step1;
  const {
    harDanskAdresse,
    firma,
    firmaRelation,
    startDato,
    emailArbejde,
    principal,
    firmaStartDato,
    emailPrivat,
  } = step2;
  return {
    createPerson: "Advokatfuldmaegtig",
    efternavn,
    fornavne,
    emailPrivat: emailPrivat ?? null,
    foedselsDato: foedselsDato ? format(foedselsDato, "yyyy-MM-dd") : null,
    cprnummer:
      cprNr && foedselsDato
        ? `${format(foedselsDato, "ddMMyy")}-${cprNr}`
        : null,
    principalId: principal ? principal.id : "",
    udenlandskAdvokatsamfundId: null,
    personFirma:
      harDanskAdresse && firma
        ? {
            adresseStandard: true,
            firmaId: firma?.id,
            periodeFraDato: firmaStartDato
              ? format(firmaStartDato, "yyyy-MM-dd")
              : "",
            personfirmatypeNr: firmaRelation.kode,
            emailArbejde: emailArbejde ? emailArbejde : undefined,
          }
        : undefined,
    tlfMobilNr: tlfMobilNr,
    startDato: startDato ? format(startDato, "yyyy-MM-dd") : "",
    koen: koen !== "" ? koen : null,
  };
};
const getAdvokatPersonDTO = (form: {
  step1: OpretPersonStep1Formik;
  step2: OpretPersonStep2Formik;
}): OpretPersonDTO => {
  const { step1, step2 } = form;
  const { cprNr, efternavn, foedselsDato, fornavne, tlfMobilNr, koen } = step1;
  const {
    harDanskAdresse,
    firma,
    firmaRelation,
    startDato,
    emailArbejde,
    straksDeponering,
    beskikkelseAarsag,
    firmaStartDato,
    emailPrivat,
  } = step2;

  return {
    createPerson: straksDeponering ? "AdvokatStraksdeponering" : "Advokat",
    efternavn,
    fornavne,
    emailPrivat: emailPrivat ?? null,
    foedselsDato: foedselsDato ? format(foedselsDato, "yyyy-MM-dd") : null,
    cprnummer:
      cprNr && foedselsDato
        ? `${format(foedselsDato, "ddMMyy")}-${cprNr}`
        : null,
    tlfMobilNr,
    personFirma:
      harDanskAdresse && firma
        ? {
            adresseStandard: true,
            firmaId: firma?.id,
            periodeFraDato: firmaStartDato
              ? format(firmaStartDato, "yyyy-MM-dd")
              : "",
            personfirmatypeNr: firmaRelation.kode,
            emailArbejde: emailArbejde ? emailArbejde : undefined,
          }
        : undefined,

    principalId: null,
    startDato: startDato ? format(startDato, "yyyy-MM-dd") : "",
    udenlandskAdvokatsamfundId: null,
    beskikkelseAarsag: beskikkelseAarsag?.length ? beskikkelseAarsag : null,
    koen: koen !== "" ? koen : null,
  };
};
const getEUAdvokatPersonDTO = (form: {
  step1: OpretPersonStep1Formik;
  step2: OpretPersonStep2Formik;
}): OpretPersonDTO => {
  const { step1, step2 } = form;
  const { cprNr, efternavn, foedselsDato, fornavne, tlfMobilNr, koen } = step1;
  const {
    harDanskAdresse,
    firma,
    firmaRelation,
    startDato,
    emailArbejde,
    udenlandskAdvokatSamfund,
    beskikkelseAarsag,
    firmaStartDato,
    emailPrivat,
  } = step2;

  return {
    createPerson: "EuAdvokat",
    efternavn,
    fornavne,
    emailPrivat: emailPrivat ?? null,
    foedselsDato: foedselsDato ? format(foedselsDato, "yyyy-MM-dd") : null,
    cprnummer:
      cprNr && foedselsDato
        ? `${format(foedselsDato, "ddMMyy")}-${cprNr}`
        : null,
    tlfMobilNr,
    personFirma:
      harDanskAdresse && firma
        ? {
            adresseStandard: true,
            firmaId: firma?.id,
            periodeFraDato: firmaStartDato
              ? format(firmaStartDato, "yyyy-MM-dd")
              : "",
            personfirmatypeNr: firmaRelation.kode,
            emailArbejde: emailArbejde ? emailArbejde : undefined,
          }
        : undefined,
    principalId: null,
    startDato: startDato ? format(startDato, "yyyy-MM-dd") : "",
    udenlandskAdvokatsamfundId: udenlandskAdvokatSamfund?.id ?? "",
    beskikkelseAarsag: beskikkelseAarsag?.length ? beskikkelseAarsag : null,
    koen: koen !== "" ? koen : null,
  };
};
export interface CreatePersonFirmaDTO {
  personfirmatypeNr: "Ansat" | "Partner" | "Ejer" | "Stifter" | "AnsatOgsaaI";
  periodeFraDato: string;
  periodeTilDatoInklusiv?: string;
  bemaerkninger?: string;
  adresseStandard: boolean;
  adressePrioritet?: number;
  emailArbejde?: string;
  firmaId: string;
}
interface OpretPersonDTO {
  createPerson:
    | "Oevrig"
    | "Advokatfuldmaegtig"
    | "Advokat"
    | "AdvokatStraksdeponering"
    | "EuAdvokat";
  fornavne: string;
  efternavn: string;
  foedselsDato: string | null;
  cprnummer: string | null;
  beskikkelseAarsag?: string | null;
  tlfMobilNr?: string;
  emailPrivat?: string;
  startDato?: string;
  principalId: string | null;
  udenlandskAdvokatsamfundId: string | null;
  personFirma?: CreatePersonFirmaDTO;
  personKontaktadresse?: PersonKontaktAdresseDTO | null;
  koen: "M" | "K" | "N" | null;
}
export type CreatePersonKode =
  | { kode: "Oevrig"; beskrivelse: "Øvrig" }
  | { kode: "Advokatfuldmaegtig"; beskrivelse: "Advokatfuldmægtig" }
  | { kode: "Advokat"; beskrivelse: "Advokat" }
  | { kode: "AdvokatStraksdeponering"; beskrivelse: "Advokat straksdeponering" }
  | { kode: "EuAdvokat"; beskrivelse: "EU-advokat" };

export const CreatePersonKodeDict: {
  ovrig: CreatePersonKode;
  advokatfuldmaegtig: CreatePersonKode;
  advokat: CreatePersonKode;
  advokatstraksdeponering: CreatePersonKode;
  euadvokat: CreatePersonKode;
} = {
  ovrig: { kode: "Oevrig", beskrivelse: "Øvrig" },
  advokatfuldmaegtig: {
    kode: "Advokatfuldmaegtig",
    beskrivelse: "Advokatfuldmægtig",
  },
  advokat: { kode: "Advokat", beskrivelse: "Advokat" },
  advokatstraksdeponering: {
    kode: "AdvokatStraksdeponering",
    beskrivelse: "Advokat straksdeponering",
  },
  euadvokat: { kode: "EuAdvokat", beskrivelse: "EU-advokat" },
};
export type Step = 1 | 2 | 3;

export type OpretPersonStep1Formik = {
  createPerson: CreatePersonKode;
  fornavne: string;
  efternavn: string;
  foedselsDato: Date | undefined;
  cprNr: string;
  tlfMobilNr: string;
  koen: "" | "M" | "K" | "N";
};
export type OpretPersonStep2Formik = {
  harFirmaRelation: boolean;
  firma: AutoCompleteItem | undefined;
  firmaRelation: FirmaRelationKode;
  emailArbejde: string;
  emailPrivat: string;
  harDanskAdresse: boolean;
  danskAdresse?: AdresseDTO;
  andenAdresse: IAdresseGeneric | null;
  startDato: Date | undefined;
  firmaStartDato: Date | undefined;
  principal: AutoCompleteItem | undefined;
  straksDeponering: boolean;
  udenlandskAdvokatSamfund: IUdenlandskAdvokatsamfund | undefined;
  attention: string;
  bemaerkning: string;
  coNavn: string;
  periodeFraDato: Date | undefined;
  periodeTilDatoInklusiv?: Date | undefined;
  beskikkelseAarsag?: string;
};

export type FirmaRelationKode =
  | { kode: "Ansat"; beskrivelse: "Ansat" }
  | { kode: "Partner"; beskrivelse: "Partner" }
  | { kode: "Ejer"; beskrivelse: "Ejer" };

export const FirmaRelationKodeDict: {
  ansat: FirmaRelationKode;
  partner: FirmaRelationKode;
  ejer: FirmaRelationKode;
} = {
  ansat: { kode: "Ansat", beskrivelse: "Ansat" },
  partner: {
    kode: "Partner",
    beskrivelse: "Partner",
  },
  ejer: { kode: "Ejer", beskrivelse: "Ejer" },
};
